import Tween from 'gsap-promise';

import { bindEvents, lerp, clamp, offset, getPseudoProperty, addListener, removeListener } from '../../assets/js/helpers';

export const selector = 'main-nav';

export default class MainNav {

	constructor(section) {
		this.el = {
			section,
			showButton: section.querySelector(`.${selector}__menu-button`),
			hideButton: section.querySelector(`.${selector}__close`),
			scrollBar: section.querySelector(`.${selector}__scroll-indicator`),
			linksWrapper: section.querySelector('nav'),
			links: section.querySelectorAll('nav li button'),
			pageSections: document.querySelectorAll('[data-background-color]')
		};

		this.state = null;

		this.targetScroll = window.pageYOffset;
		this.easedScroll = window.pageYOffset;

		this.ease = 0.1;
		this.height = document.body.scrollHeight;
		this.winHeight = window.innerHeight;

		this.currentsection = null;
		this.offsets = [];

		this.scrollObj = { top: 0 };

		this.flags = {
			scrollActive: false
		};

		this.events = bindEvents(this);
		this.addEventListeners();
	}

	checkState() {
		this.state = getPseudoProperty(this.el.section, 'content');
	}

	measure() {
		this.winHeight = window.innerHeight;
		this.height = (document.body.scrollHeight - this.winHeight);

		this.offsets = [];

		for (const section of this.el.pageSections) {
			const top = offset.top(section);
			const h = parseFloat(section.scrollHeight);
			const bottom = (top + h);

			this.offsets.push({
				el: section,
				top,
				bottom
			});
		}
	}

	updatePageBackground() {
		if (this.offsets.length > 0) {
			const margin = this.state === 'sticky' ? (this.winHeight * 0.65) : 65;
			const scroll = this.targetScroll + margin;

			for (let i = 0; i < this.offsets.length; i++) {
				const section = this.offsets[i];

				if (!this.section || (scroll >= section.top && scroll < section.bottom && section.el !== this.section.el)) {
					this.section = section;

					const background = this.section.el.getAttribute('data-background-color');

					document.body.classList.remove('page--dark');
					document.body.classList.remove('page--medium');
					document.body.classList.remove('page--light');

					document.body.classList.add(`page--${background}`);
				}
			}
		}
	}

	scrollToSection(sectionId) {
		const section = document.querySelector(`#${sectionId}`);

		if (!section) {
			return false;
		}

		this.scrollObj.top = window.pageYOffset;

		let top = offset.top(section);

		top -= window.innerHeight * 0.2;

		TweenMax.killTweensOf(this.scrollObj);
		Tween.to(this.scrollObj, 1.0, {
			top,
			ease: Power3.easeInOut,
			onUpdate: () => {
				window.scrollTo({ top: this.scrollObj.top });
			}
		});

		if (this.state === 'scroll') {
			this.el.section.classList.remove('is-active');
		}
	}

	_onTick() {
		this.easedScroll = lerp(this.easedScroll, this.targetScroll, this.ease);

		const percentage = parseFloat(this.easedScroll / this.height);

		if (this.state === 'sticky') {
			Tween.set(this.el.scrollBar, {
				scaleY: percentage
			});
		} else {
			Tween.set(this.el.scrollBar, {
				clearProps: 'scaleY'
			});
		}
	}

	_onWindowScroll() {
		// scrollbar position
		this.targetScroll = clamp(window.pageYOffset, 0, this.height);

		// show / hide
		if (this.state === 'sticky') {
			if (this.targetScroll >= this.winHeight && !this.flags.scrollActive) {
				this.flags.scrollActive = true;
				this.el.section.classList.add('is-active');
			} else if (this.targetScroll < this.winHeight && this.flags.scrollActive) {
				this.flags.scrollActive = false;
				this.el.section.classList.remove('is-active');
			}
		}

		// page backgrounds
		this.updatePageBackground();
	}

	_onShowButtonClick(e) {
		e.preventDefault();

		if (this.state === 'scroll') {
			this.el.section.classList.add('is-active');
		}
	}

	_onHideButtonClick(e) {
		e.preventDefault();

		if (this.state === 'scroll') {
			this.el.section.classList.remove('is-active');
		}
	}

	_onTouchMove(e) {
		if (this.state === 'scroll') {
			e.preventDefault();
		}
	}

	_onWindowLoad() {
		this.checkState();
		this.measure();
		this.events._onWindowScroll();
	}

	_onWindowResize() {
		this.checkState();
		this.measure();
	}

	_onLinkClick(e) {
		const link = e.currentTarget;
		const sectionId = link.getAttribute('data-target');

		this.scrollToSection(sectionId);
	}

	addEventListeners() {
		this.el.showButton.addEventListener('click', this.events._onShowButtonClick);
		this.el.hideButton.addEventListener('click', this.events._onHideButtonClick);
		this.el.linksWrapper.addEventListener('touchmove', this.events._onTouchMove);
		window.addEventListener('resize', this.events._onWindowResize);
		window.addEventListener('load', this.events._onWindowLoad);
		window.addEventListener('scroll', this.events._onWindowScroll);
		TweenMax.ticker.addEventListener('tick', this.events._onTick);
		addListener(this.el.links, 'click', this.events._onLinkClick);
	}

	removeEventListeners() {
		this.el.showButton.removeEventListener('click', this.events._onShowButtonClick);
		this.el.hideButton.removeEventListener('click', this.events._onHideButtonClick);
		this.el.linksWrapper.removeEventListener('touchmove', this.events._onTouchMove);
		window.removeEventListener('resize', this.events._onWindowResize);
		window.removeEventListener('load', this.events._onWindowLoad);
		window.removeEventListener('scroll', this.events._onWindowScroll);
		TweenMax.ticker.removeEventListener('tick', this.events._onTick);
		removeListener(this.el.links, 'click', this.events._onLinkClick);
	}

	destroy() {
		this.removeEventListeners();
	}

}
